import { useEffect, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ToolInfoSection } from "@/components/ToolInfoSection";


const CODES: Record<number, { text: string; desc: string; category: string; nextStep?: string }> =
  {
    100: {
      text: "Continue",
      desc: "The server received the request headers and the client should proceed to send the request body.",
      category: "Informational",
    },
    101: {
      text: "Switching Protocols",
      desc: "The server is switching to a different protocol as requested by the client.",
      category: "Informational",
    },
    102: {
      text: "Processing",
      desc: "The server accepted the full request but hasn't finished processing it yet.",
      category: "Informational",
    },
    103: {
      text: "Early Hints",
      desc: "The server sends some response headers early, before the final response is ready.",
      category: "Informational",
    },
    200: { text: "OK", desc: "The request succeeded.", category: "Success" },
    201: {
      text: "Created",
      desc: "The request succeeded and a new resource was created.",
      category: "Success",
    },
    202: {
      text: "Accepted",
      desc: "The request was accepted for processing, but that processing isn't complete yet.",
      category: "Success",
    },
    203: {
      text: "Non-Authoritative Information",
      desc: "The returned metadata is from a proxy or cache, not the exact metadata from the origin server.",
      category: "Success",
    },
    204: {
      text: "No Content",
      desc: "The request succeeded but there's no content to return.",
      category: "Success",
    },
    205: {
      text: "Reset Content",
      desc: "The server is telling the client to reset the document view that sent the request.",
      category: "Success",
    },
    206: {
      text: "Partial Content",
      desc: "The server is delivering only part of the resource, matching a range requested by the client.",
      category: "Success",
    },
    226: {
      text: "IM Used",
      desc: "The server fulfilled the request using a delta encoding (a set of instance manipulations) against a base version.",
      category: "Success",
    },
    300: {
      text: "Multiple Choices",
      desc: "There's more than one possible response, and the client should choose one.",
      category: "Redirection",
    },
    301: {
      text: "Moved Permanently",
      desc: "The resource has permanently moved to a new URL.",
      category: "Redirection",
    },
    302: {
      text: "Found",
      desc: "The resource temporarily resides at a different URL.",
      category: "Redirection",
    },
    303: {
      text: "See Other",
      desc: "The client should retrieve the resource with a GET request to a different URL.",
      category: "Redirection",
    },
    304: {
      text: "Not Modified",
      desc: "The cached version is still valid; no need to re-fetch.",
      category: "Redirection",
    },
    305: {
      text: "Use Proxy",
      desc: "Deprecated — the requested resource had to be accessed through the proxy given in the response.",
      category: "Redirection",
    },
    306: {
      text: "(Unused)",
      desc: "Reserved from an earlier version of the spec ('Switch Proxy'), no longer used.",
      category: "Redirection",
    },
    307: {
      text: "Temporary Redirect",
      desc: "The resource temporarily resides at a different URL, and the request method must not change.",
      category: "Redirection",
    },
    308: {
      text: "Permanent Redirect",
      desc: "The resource has permanently moved to a new URL, and the request method must not change.",
      category: "Redirection",
    },
    400: {
      text: "Bad Request",
      desc: "The server can't process the request due to a client error.",
      category: "Client Error",
      nextStep: "Check the request syntax and payload for malformed JSON, missing required fields, or invalid parameter types.",
    },
    401: {
      text: "Unauthorized",
      desc: "Authentication is required and has failed or not been provided.",
      category: "Client Error",
      nextStep: "Verify the request includes valid authentication credentials (like a token or API key) and that they haven't expired.",
    },
    402: {
      text: "Payment Required",
      desc: "Reserved for future use with digital payment systems — not commonly implemented.",
      category: "Client Error",
      nextStep: "Rarely used in practice — check the specific API's docs, since this usually signals a billing or quota issue on that platform.",
    },
    403: {
      text: "Forbidden",
      desc: "The server understood the request but refuses to authorize it.",
      category: "Client Error",
      nextStep: "The credentials are valid but lack permission — check the user's role or scope, not whether they're logged in.",
    },
    404: {
      text: "Not Found",
      desc: "The requested resource couldn't be found.",
      category: "Client Error",
      nextStep: "Double-check the URL path for typos, and confirm the resource still exists at that endpoint.",
    },
    405: {
      text: "Method Not Allowed",
      desc: "The HTTP method isn't supported for this resource.",
      category: "Client Error",
      nextStep: "Confirm you're using the HTTP method (GET/POST/PUT/DELETE) the endpoint actually supports.",
    },
    406: {
      text: "Not Acceptable",
      desc: "The server can't produce a response matching the content the client said it would accept.",
      category: "Client Error",
      nextStep: "Check the Accept header you're sending matches a format the server can actually return.",
    },
    407: {
      text: "Proxy Authentication Required",
      desc: "The client must authenticate itself with a proxy before the request can proceed.",
      category: "Client Error",
      nextStep: "Authenticate with the proxy server itself, separately from the destination server's own authentication.",
    },
    408: {
      text: "Request Timeout",
      desc: "The server timed out waiting for the request.",
      category: "Client Error",
      nextStep: "Check for slow network conditions or a client holding the connection open too long — consider retrying.",
    },
    409: {
      text: "Conflict",
      desc: "The request conflicts with the current state of the resource.",
      category: "Client Error",
      nextStep: "The request conflicts with the resource's current state (like a duplicate entry) — refetch the current state before retrying.",
    },
    410: {
      text: "Gone",
      desc: "The resource is permanently gone and isn't coming back, unlike a plain 404.",
      category: "Client Error",
      nextStep: "Unlike 404, this resource was intentionally removed — update any links pointing to it rather than expecting it to return.",
    },
    411: {
      text: "Length Required",
      desc: "The server requires a Content-Length header, which the request didn't include.",
      category: "Client Error",
      nextStep: "Add a Content-Length header to the request.",
    },
    412: {
      text: "Precondition Failed",
      desc: "A condition set in the request's headers wasn't met by the server.",
      category: "Client Error",
      nextStep: "A conditional header you sent (like If-Match) didn't match the resource's current state — refetch and retry.",
    },
    413: {
      text: "Content Too Large",
      desc: "The request body is larger than the server is willing or able to process.",
      category: "Client Error",
      nextStep: "Reduce the request body size, or check the server's configured upload limit.",
    },
    414: {
      text: "URI Too Long",
      desc: "The requested URL is longer than the server is willing to interpret.",
      category: "Client Error",
      nextStep: "Shorten the URL, or move parameters from the query string into the request body if the method allows it.",
    },
    415: {
      text: "Unsupported Media Type",
      desc: "The server doesn't support the format of the request payload.",
      category: "Client Error",
      nextStep: "Check the Content-Type header matches a format the server actually accepts.",
    },
    416: {
      text: "Range Not Satisfiable",
      desc: "The requested byte range doesn't match the actual size of the resource.",
      category: "Client Error",
      nextStep: "The requested byte range is outside the resource's actual size — verify the range values you're sending.",
    },
    417: {
      text: "Expectation Failed",
      desc: "The server can't meet the requirement stated in the request's Expect header.",
      category: "Client Error",
      nextStep: "Remove the Expect header, or check whether the server actually supports it.",
    },
    418: {
      text: "I'm a teapot",
      desc: "An April Fools' joke from RFC 2324 — the server refuses to brew coffee because it's a teapot.",
      category: "Client Error",
      nextStep: "This is an April Fools' joke from RFC 2324 (Hyper Text Coffee Pot Control Protocol) — not a real error to handle.",
    },
    421: {
      text: "Misdirected Request",
      desc: "The request was sent to a server that isn't able to produce a response for it.",
      category: "Client Error",
      nextStep: "Reconfirm this connection or server can actually handle requests for the target host.",
    },
    422: {
      text: "Unprocessable Content",
      desc: "The request was well-formed but contains semantic errors.",
      category: "Client Error",
      nextStep: "The syntax is valid but the semantics aren't (e.g. failed validation) — check the field-level error the server usually includes.",
    },
    423: {
      text: "Locked",
      desc: "The resource being accessed is locked (used in WebDAV).",
      category: "Client Error",
      nextStep: "The resource is locked (common in WebDAV) — wait, or request that it be unlocked.",
    },
    424: {
      text: "Failed Dependency",
      desc: "The request failed because a previous, related request also failed (used in WebDAV).",
      category: "Client Error",
      nextStep: "A prior required request in the same operation failed — check that earlier step, not this one.",
    },
    425: {
      text: "Too Early",
      desc: "The server isn't willing to risk processing a request that might be replayed.",
      category: "Client Error",
      nextStep: "The server is guarding against replay attacks on early data — retry once the full connection is established.",
    },
    426: {
      text: "Upgrade Required",
      desc: "The server refuses to process the request using the current protocol and wants the client to upgrade.",
      category: "Client Error",
      nextStep: "Switch to the protocol version named in the Upgrade header the server sent back.",
    },
    428: {
      text: "Precondition Required",
      desc: "The server requires the request to be conditional, to prevent lost-update race conditions.",
      category: "Client Error",
      nextStep: "Add the conditional header (like If-Match) the server is requiring before it'll process the request.",
    },
    429: {
      text: "Too Many Requests",
      desc: "The client has sent too many requests in a given time.",
      category: "Client Error",
      nextStep: "You're being rate-limited — check the Retry-After header and slow down your request rate.",
    },
    431: {
      text: "Request Header Fields Too Large",
      desc: "The request's header fields are too large for the server to process.",
      category: "Client Error",
      nextStep: "Reduce the size or number of headers you're sending.",
    },
    451: {
      text: "Unavailable For Legal Reasons",
      desc: "The server is denying access to the resource as a result of a legal demand.",
      category: "Client Error",
      nextStep: "The resource is blocked for legal or regulatory reasons — this isn't something to work around technically.",
    },
    500: {
      text: "Internal Server Error",
      desc: "The server encountered an unexpected condition.",
      category: "Server Error",
      nextStep: "Check the server's own logs — this is a generic catch-all, and the real cause lives server-side, not in the request.",
    },
    501: {
      text: "Not Implemented",
      desc: "The server doesn't support the functionality required to fulfill the request.",
      category: "Server Error",
      nextStep: "The server doesn't support the requested method — confirm you're calling a real, implemented endpoint.",
    },
    502: {
      text: "Bad Gateway",
      desc: "The server, acting as a gateway, got an invalid response upstream.",
      category: "Server Error",
      nextStep: "An upstream server (behind a proxy or load balancer) sent an invalid response — check that upstream service's health.",
    },
    503: {
      text: "Service Unavailable",
      desc: "The server isn't ready to handle the request (overloaded or down).",
      category: "Server Error",
      nextStep: "The server is temporarily overloaded or down for maintenance — check the Retry-After header and back off.",
    },
    504: {
      text: "Gateway Timeout",
      desc: "The upstream server failed to respond in time.",
      category: "Server Error",
      nextStep: "An upstream server took too long to respond — check that upstream service's performance, not the client's request.",
    },
    505: {
      text: "HTTP Version Not Supported",
      desc: "The server doesn't support the HTTP protocol version used in the request.",
      category: "Server Error",
      nextStep: "Use a version of HTTP the server actually supports.",
    },
    506: {
      text: "Variant Also Negotiates",
      desc: "The server has an internal configuration error in its content negotiation setup.",
      category: "Server Error",
      nextStep: "A server-side content negotiation misconfiguration — needs a fix on the server, not the client.",
    },
    507: {
      text: "Insufficient Storage",
      desc: "The server can't store the representation needed to complete the request (used in WebDAV).",
      category: "Server Error",
      nextStep: "The server has run out of storage to complete the request — a server-side capacity issue.",
    },
    508: {
      text: "Loop Detected",
      desc: "The server detected an infinite loop while processing the request (used in WebDAV).",
      category: "Server Error",
      nextStep: "The server detected an infinite loop while processing — check for a circular reference in the request chain.",
    },
    510: {
      text: "Not Extended",
      desc: "Further extensions to the request are required for the server to fulfill it.",
      category: "Server Error",
      nextStep: "The server requires an extension not specified in the request — check the server's documentation for what's required.",
    },
    511: {
      text: "Network Authentication Required",
      desc: "The client needs to authenticate to gain network access, often seen with captive Wi-Fi portals.",
      category: "Server Error",
      nextStep: "Authenticate with the network itself first (common with public/hotel Wi-Fi captive portals) before the actual request will succeed.",
    },
  };

const CATEGORY_COLOR: Record<string, string> = {
  Informational: "text-slate-600 bg-slate-50 dark:bg-slate-900",
  Success: "text-green-600 bg-green-50 dark:bg-green-950",
  Redirection: "text-blue-600 bg-blue-50 dark:bg-blue-950",
  "Client Error": "text-orange-600 bg-orange-50 dark:bg-orange-950",
  "Server Error": "text-red-600 bg-red-50 dark:bg-red-950",
};

const TOOL_INFO_CONTENT = {
  what: `A searchable reference for HTTP status codes, explaining what each one means, which category (success, redirect, client error, server error) it falls into, and for error codes specifically, a concrete next step for what to actually check or do about it.`,
  how: `Search by status code number or by name, and the matching entries with their explanations (and, for error codes, a suggested next step) are displayed.`,
  tip: `The first digit of any status code tells you the general category at a glance — 2xx means success, 4xx means the client made a mistake, and 5xx means the server had a problem, even before you look up the specific code.`,
};

export default function HttpStatusReference() {
  const [search, setSearch] = useState("");
  const [cls, setCls] = useState<string>("all");

  // Permalinks: /tool/http-status-reference#404 scrolls to that code.
  useEffect(() => {
    const hash = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
    if (/^\d{3}$/.test(hash) && CODES[Number(hash)]) {
      setTimeout(() => document.getElementById(`status-${hash}`)?.scrollIntoView({ block: "center" }), 100);
    }
  }, []);

  const filtered = useMemo(() => {
    const entries = Object.entries(CODES).filter(([code]) => cls === "all" || code.startsWith(cls));
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      ([code, info]) =>
        code.includes(q) ||
        info.text.toLowerCase().includes(q) ||
        info.category.toLowerCase().includes(q)
    );
  }, [search, cls]);

  // Same reasoning as Government Forms Hub: this is a static,
  // built-in reference table with search/filter, nothing generated or
  // downloaded -- the useLogToolUsage(true, 3000) call here was a
  // misapplication of the hook, not a gating gap to fix.
  return (
    <ToolContainer>
      <Input
        placeholder="Search by code or name (e.g. 404, Not Found)..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="mb-3"
        aria-label="Search HTTP status codes"
      />
      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter by status class">
        {[["all", "All"], ["1", "1xx"], ["2", "2xx"], ["3", "3xx"], ["4", "4xx"], ["5", "5xx"]].map(([v, l]) => (
          <button
            key={v}
            type="button"
            aria-pressed={cls === v}
            onClick={() => setCls(v)}
            className={`px-3 py-1 rounded-full border text-xs font-medium ${cls === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.map(([code, info]) => (
          <Card key={code} id={`status-${code}`} className="p-4 flex items-start gap-4 scroll-mt-24">
            <a
              href={`#${code}`}
              title={`Link to ${code}`}
              className={`px-2.5 py-1 rounded-md font-mono font-bold text-sm shrink-0 ${CATEGORY_COLOR[info.category]}`}
            >
              {code}
            </a>
            <div>
              <p className="font-medium">{info.text}</p>
              <p className="text-sm text-muted-foreground">{info.desc}</p>
              {info.nextStep && (
                <p className="text-xs text-primary mt-1.5">
                  <span className="font-medium">What to do: </span>{info.nextStep}
                </p>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No matching status codes
          </p>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
